import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { set } from 'lodash/fp';
import { useConfirm } from '@leagueplatform/web-common';
import { EnrollmentConfigPresenter } from './enrollment-config.view';
import {
  visitEnrollmentConfig as visitEnrollmentConfigAction,
  fetchBenefitPlanOptions as fetchBenefitPlanOptionsAction,
  setBenefitPlanOptions as setBenefitPlanOptionsAction,
  setCurrentPlanId as setCurrentPlanIdAction,
  setCurrentBundlePath as setCurrentBundlePathAction,
  formValuesReset as formValuesResetAction,
  createBenefitPlanOptions as createBenefitPlanOptionsAction,
  getEmployerBenefitPlans as getEmployerBenefitPlansAction,
} from './enrollment-config.actions';
import {
  selectDenormalizedPlanOptions,
  selectBenefitPlanOptionsSchema,
  selectActiveBenefitPlanTreeData,
  selectSubmissionError,
  selectCurrentPlanId,
  selectCurrentBundlePath,
  selectMissingPlanOptions,
} from './enrollment-config.selectors';
import { selectBenefitPlans } from '../Benefits/BenefitClass/Plans/selectors';
import { useActiveNode } from './tree-navigation';
import { JSONSchemaDefinitionPropType } from 'common/json-schema-form/prop-types';
import { getSchemaDefinitionForRef } from 'common/json-schema-form/json-schema-form.container';
import {
  matchPlanIdRegex,
  capturePlanUpdatePath,
  matchBundlePath,
} from './enrollment-config.regexes';
import { StepContainer } from '../enrollment-design/step-container/step-container.view';
import { schemaMetadata } from './schema-metadata';

const parseFormDataPath = pathString => {
  const path = pathString || '';
  const planId = get(path.match(matchPlanIdRegex), 1);
  const updatePath = get(path.match(capturePlanUpdatePath), 1);
  const bundlePath = get(path.match(matchBundlePath), 0);
  return [planId, updatePath, bundlePath];
};

export const EnrollmentConfigContainer = ({
  benefitPlanOptions,
  benefitPlanOptionsSchema,
  benefitPlans,
  missingPlanOptions,
  data,
  fetchBenefitPlanOptions,
  formValuesReset,
  groupId,
  setBenefitPlanOptions,
  setCurrentPlanId,
  setCurrentBundlePath,
  visitEnrollmentConfig,
  currentPlanId,
  currentBundlePath,
  getEmployerBenefitPlans,
  selectedPlanId,
  treeData,
  ...props
}) => {
  const [formDataPath, setFormDataPath] = useState('');
  const [formData, setFormData] = useState(data);
  const [$ref, set$Ref] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { setIsConfirming: setIsConfirmingDataChangedModal, ...confirmProps } =
    useConfirm();
  const { activeNode, setActiveNode } = useActiveNode({
    treeData,
    setFormData,
    setFormDataPath,
    set$Ref,
    setValidationWarnings,
  });
  useEffect(() => {
    visitEnrollmentConfig();
  }, [visitEnrollmentConfig]);
  useEffect(() => {
    // This is required when we directly navigate to step 2 url with planId in the params
    if (selectedPlanId && selectedPlanId !== currentPlanId) {
      setCurrentPlanId(selectedPlanId);
    }
  }, [selectedPlanId, currentPlanId, setCurrentPlanId]);
  useEffect(() => {
    const [, , bundlePath] = parseFormDataPath(formDataPath);
    if (bundlePath && bundlePath !== currentBundlePath) {
      setCurrentBundlePath(bundlePath);
    }
  }, [formDataPath, setCurrentBundlePath, currentBundlePath]);
  useEffect(() => {
    getEmployerBenefitPlans({
      groupId,
      planId: selectedPlanId,
    });

    // Fetching benefit plan options on initial load
    fetchBenefitPlanOptions({
      groupId,
      planId: selectedPlanId,
    });
  }, [
    groupId,
    selectedPlanId,
    getEmployerBenefitPlans,
    fetchBenefitPlanOptions,
  ]);

  const onToggleTreeNode = ({ node, handleToggle }) => {
    if (hasUnsavedChanges) {
      // show modal as a warning for the user to save the form
      setIsConfirmingDataChangedModal(true);
    } else {
      handleToggle({ node });
    }
  };

  const getPlanForPath = dataPath => {
    const [planId, updatePath] = parseFormDataPath(dataPath);
    const plan = benefitPlanOptions[planId];
    if (!plan) {
      throw new Error(
        `No Benefit Plan Options Found For plan_id: ${planId} in ${benefitPlanOptions}`,
      );
    }
    return [plan, updatePath];
  };

  /**
   * @function onSubmit this is passed into the JSONSchemaForm > EntityForm's Formik implementation
   * see https://jaredpalmer.com/formik/docs/api/formik#onsubmit-values-values-formikbag-formikbag-void
   *
   * @param {} nextData forms current values which we pass to setBenefitPlanOptions to save values to the server
   * @param {formikBag} FormikBag see: https://jaredpalmer.com/formik/docs/api/withformik#the-formikbag
   */

  const onSubmit = (nextData, { setSubmitting }) => {
    try {
      const [previousPlanOptions, updatePath] = getPlanForPath(formDataPath);

      const nextPlanOptions = updatePath
        ? set(updatePath, nextData, previousPlanOptions)
        : nextData;

      setBenefitPlanOptions(nextPlanOptions);
      setHasUnsavedChanges(false);
    } catch {
      setSubmitting(false);
    }
  };

  /**
   * @function onReset this is passed into the JSONSchemaForm > EntityForm's Formik implementation
   * see: https://jaredpalmer.com/formik/docs/api/formik#onreset-values-values-formikbag-formikbag-void
   *
   * @param {*} _ forms current values, we don't care about this ATM!
   * @param {formikBag} FormikBag see: https://jaredpalmer.com/formik/docs/api/withformik#the-formikbag
   */

  const onReset = () => {
    formValuesReset(); // this is a redux action to clear any form errors
  };

  const onAddNode = () => {
    const { children, entityDataPath, treePath, schemaRef } = activeNode;
    const schemaDefinition = getSchemaDefinitionForRef(
      benefitPlanOptionsSchema,
      schemaRef,
    );
    const index = get(children, 'length', 0);
    const newNode = {
      treePath: `${treePath}.children.${index}`,
      entityDataPath: `${entityDataPath}.${index}`,
      entityData: [],
      schemaRef: get(schemaDefinition, 'items.$ref'),
      name: `New ${schemaDefinition.title}`,
      fromCollectionView: true,
    };
    setActiveNode(newNode);
  };

  const onRemoveNode = node => {
    const [previousPlanOptions, updatePath] = getPlanForPath(
      node.entityDataPath,
    );
    const splitPath = updatePath.split('.');
    const indexToRemove = Number(splitPath.pop());
    const siblings = get(previousPlanOptions, splitPath);

    const siblingsWithoutItem = siblings.filter(
      (_, idx) => idx !== indexToRemove,
    );

    const nextPlanOptions = set(
      splitPath,
      siblingsWithoutItem,
      previousPlanOptions,
    );

    setBenefitPlanOptions(nextPlanOptions);
  };

  const showMissingPlanOptions = missingPlanOptions.includes(selectedPlanId);
  const benefitPlan =
    selectedPlanId && benefitPlans.find(plan => plan.id === selectedPlanId);

  return (
    <StepContainer title="BENEFIT_CONFIG_TITLE" selectedPlanId={selectedPlanId}>
      <EnrollmentConfigPresenter
        // eslint-disable-next-line react/jsx-props-no-spreading -- FIXME: automatically added for existing issue
        {...props}
        // eslint-disable-next-line react/jsx-props-no-spreading -- FIXME: automatically added for existing issue
        {...confirmProps}
        formData={formData}
        treeData={treeData}
        onRemoveNode={onRemoveNode}
        onSubmit={onSubmit}
        onReset={onReset}
        onToggleTreeNode={onToggleTreeNode}
        schema={benefitPlanOptionsSchema}
        schemaMetadata={schemaMetadata}
        $ref={$ref}
        set$Ref={set$Ref}
        setFormData={setFormData}
        setFormDataPath={setFormDataPath}
        activeNode={activeNode}
        setActiveNode={setActiveNode}
        onAddNode={onAddNode}
        setHasUnsavedChanges={setHasUnsavedChanges}
        showMissingPlanOptions={showMissingPlanOptions}
        benefitPlan={benefitPlan}
        validationWarnings={validationWarnings}
      />
    </StepContainer>
  );
};

EnrollmentConfigContainer.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types -- FIXME: automatically added for existing issue
  benefitPlanOptions: PropTypes.objectOf(PropTypes.object),
  benefitPlanOptionsSchema: JSONSchemaDefinitionPropType.isRequired,
  // eslint-disable-next-line react/forbid-prop-types -- FIXME: automatically added for existing issue
  benefitPlans: PropTypes.arrayOf(PropTypes.any),
  missingPlanOptions: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line react/forbid-prop-types -- FIXME: automatically added for existing issue
  data: PropTypes.objectOf(PropTypes.any),
  fetchBenefitPlanOptions: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  setBenefitPlanOptions: PropTypes.func.isRequired,
  formValuesReset: PropTypes.func.isRequired,
  setCurrentPlanId: PropTypes.func.isRequired,
  setCurrentBundlePath: PropTypes.func.isRequired,
  visitEnrollmentConfig: PropTypes.func.isRequired,
  currentPlanId: PropTypes.string,
  currentBundlePath: PropTypes.string,
  getEmployerBenefitPlans: PropTypes.func,
  selectedPlanId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types -- FIXME: automatically added for existing issue
  treeData: PropTypes.objectOf(PropTypes.any),
};

EnrollmentConfigContainer.defaultProps = {
  benefitPlanOptions: undefined,
  benefitPlans: [],
  missingPlanOptions: [],
  data: null,
  currentPlanId: undefined,
  currentBundlePath: undefined,
  getEmployerBenefitPlans: () => {},
  treeData: {},
};

const mapStateToProps = state => ({
  benefitPlanOptions: selectDenormalizedPlanOptions(state),
  benefitPlanOptionsSchema: selectBenefitPlanOptionsSchema(state),
  benefitPlans: selectBenefitPlans(state),
  missingPlanOptions: selectMissingPlanOptions(state),
  treeData: selectActiveBenefitPlanTreeData(state),
  submissionError: selectSubmissionError(state),
  currentPlanId: selectCurrentPlanId(state),
  currentBundlePath: selectCurrentBundlePath(state),
});

const mapDispatchToProps = {
  fetchBenefitPlanOptions: fetchBenefitPlanOptionsAction,
  visitEnrollmentConfig: visitEnrollmentConfigAction,
  setBenefitPlanOptions: setBenefitPlanOptionsAction,
  setCurrentPlanId: setCurrentPlanIdAction,
  setCurrentBundlePath: setCurrentBundlePathAction,
  formValuesReset: formValuesResetAction,
  createBenefitPlanOptions: createBenefitPlanOptionsAction,
  getEmployerBenefitPlans: getEmployerBenefitPlansAction,
};

export const EnrollmentConfig = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EnrollmentConfigContainer);
